/**
 * Uzysk dowolnej połaci w Polsce, straty z cienia i przebieg dnia (projekt dachu 3D).
 *
 * METODA: transpozycja kalibrowana do tabeli PVGIS (`../pvgisPolska.js`: PVGIS 5.2 SARAH2,
 * straty 14%, 16 środków województw × 6 ustawień dachu).
 * 1. Tabelę przenosimy do punktu (lat, lon) ważeniem odwrotnością kwadratu odległości
 *    z 4 najbliższych środków województw (w samym środku wartości dokładne).
 * 2. Dla każdego miesiąca dzień wzorcowy (15.) co 15 min: pozycja słońca i DNI przy czystym
 *    niebie (Ineichen i Perez, TL 3,5). Dla płaszczyzny o nachyleniu β:
 *      Rb = Σ DNI·cosθ·IAM(θ) / Σ DNI·sin h        (wiązka, ważona czystym niebem)
 *      niebo = (1 + cos β)/2 · IAM_d,  grunt = ρ·(1 − cos β)/2 · IAM_g,  ρ = 0,2
 *    IAM wg ASHRAE (b0 = 0,05); dla nieba i gruntu kąty efektywne Brandemuehla i Beckmana.
 *    Model: POA/GHI = fb·Rb + (1 − fb)·niebo + grunt (niebo izotropowe).
 * 3. fb, efektywny udział wiązki w GHI (z częścią okołosłoneczną), dopasowujemy w każdym
 *    miesiącu najmniejszymi kwadratami logarytmów stosunków do „poludnie” dla ustawień
 *    poludnie_bok, plaski, jedna_polac, polnoc i wschod_zachod (średnia dwóch połaci).
 * 4. Uzysk(płaszczyzna) = PVGIS_poludnie(miesiąc) · model(płaszczyzna) / model(35°, S).
 *
 * DOKŁADNOŚĆ (sprawdzone na 16 środkach): ustawienia z tabeli odtworzone rocznie w granicach
 * ±2,4%; miesięcznie 97% wartości w ±3%, kwiecień–wrzesień do 4%, zimą do 6%. Model jest
 * symetryczny wschód/zachód, a PVGIS daje wschodowi do 2% więcej (pogodniejsze poranki),
 * stąd największe odchyłki ma „jedna_polac” (rocznie −1,3 do −2,4%).
 * Optimum dla południa wychodzi 36–38°, elewacja południowa 68–72% optimum. Elewacje
 * i strome połacie północne to ekstrapolacja poza tabelę: izotropowe niebo raczej zaniża
 * elewację północną. Wynik to średnia wieloletnia, rok do roku ±10%.
 * fb (w Polsce 0,40–0,67) obejmuje część okołosłoneczną i to, czego izotropowe niebo nie
 * odwzorowuje, więc jest wyższy niż udział samej wiązki bezpośredniej; tyle zabiera pełny
 * cień w `strataCienia` (dla południa 35° rocznie ok. 0,65–0,69).
 *
 * Wyniki dla miejsca liczymy raz i trzymamy w pamięci (siatka 0,05°). Konwencje kątów jak
 * w `slonce.js`: azymut prawdziwy od północy zgodnie z zegarem, S = 180.
 */

import {
  cosinusPadania, czasWarszawy, czysteNiebo, dzienRoku, mocModulu, naPlaszczyznie,
  pozycjaSlonca, wektorSlonca, wspolczynnikPadania,
} from './slonce'
import { UZYSK_MIESIECZNY } from '../pvgisPolska'

const RAD = Math.PI / 180

/** Środki województw użyte w zapytaniach PVGIS (tools/pobierz_pvgis.py), [lat, lon]. */
export const SRODKI_WOJEWODZTW = {
  dolnoslaskie: [51.10, 16.30],
  'kujawsko-pomorskie': [53.05, 18.40],
  lubelskie: [51.20, 22.80],
  lubuskie: [52.20, 15.20],
  lodzkie: [51.65, 19.35],
  malopolskie: [49.85, 20.30],
  mazowieckie: [52.30, 21.10],
  opolskie: [50.60, 17.90],
  podkarpackie: [49.95, 22.20],
  podlaskie: [53.35, 22.90],
  pomorskie: [54.20, 17.90],
  slaskie: [50.35, 19.00],
  swietokrzyskie: [50.75, 20.70],
  'warminsko-mazurskie': [53.85, 20.80],
  wielkopolskie: [52.35, 17.30],
  zachodniopomorskie: [53.60, 15.50],
}

/**
 * Ustawienia tabeli PVGIS w naszej konwencji (azymut = aspect PVGIS + 180). Kilka połaci
 * = równy podział mocy, jak w generatorze tabeli.
 */
export const USTAWIENIA_PVGIS = {
  poludnie: [{ nachylenie: 35, azymut: 180 }],
  poludnie_bok: [{ nachylenie: 35, azymut: 225 }],
  plaski: [{ nachylenie: 12, azymut: 180 }],
  wschod_zachod: [{ nachylenie: 30, azymut: 90 }, { nachylenie: 30, azymut: 270 }],
  jedna_polac: [{ nachylenie: 30, azymut: 90 }],
  polnoc: [{ nachylenie: 35, azymut: 0 }],
}

const POROWNYWANE = ['poludnie_bok', 'plaski', 'jedna_polac', 'polnoc', 'wschod_zachod']

/** Rok, z którego biorą się dni wzorcowe (15. każdego miesiąca). */
export const ROK_WZORCOWY = 2026
const DZIEN_WZORCOWY = 15
const KROK_KALIBRACJI_MIN = 15
const LINKE = 3.5
const ALBEDO = 0.2
const ROZMIAR_PAMIECI = 64

const ogranicz01 = (x) => (x > 0 ? Math.min(1, x) : 0)
const suma = (tab) => tab.reduce((s, x) => s + x, 0)

/* ── Tabela PVGIS w dowolnym punkcie ────────────────────────────────────── */

/** Tabela PVGIS (kWh/kWp w miesiącach, 6 ustawień) przeniesiona do punktu metodą IDW. */
export const pvgisWPunkcie = (lat, lon) => {
  const kos = Math.cos(lat * RAD)
  const odleglosci = Object.entries(SRODKI_WOJEWODZTW)
    .map(([woj, [la, lo]]) => ({ woj, d2: (lat - la) ** 2 + ((lon - lo) * kos) ** 2 }))
    .sort((a, b) => a.d2 - b.d2)
  const wynik = {}
  if (odleglosci[0].d2 < 1e-12) {
    Object.entries(UZYSK_MIESIECZNY[odleglosci[0].woj]).forEach(([ust, w]) => { wynik[ust] = [...w] })
    return wynik
  }
  const sasiedzi = odleglosci.slice(0, 4).map(({ woj, d2 }) => ({ tabela: UZYSK_MIESIECZNY[woj], waga: 1 / d2 }))
  const sumaWag = suma(sasiedzi.map((s) => s.waga))
  Object.keys(USTAWIENIA_PVGIS).forEach((ust) => {
    wynik[ust] = Array.from({ length: 12 },
      (_, m) => suma(sasiedzi.map((s) => s.waga * s.tabela[ust][m])) / sumaWag)
  })
  return wynik
}

/* ── Geometria płaszczyzny ──────────────────────────────────────────────── */

const iamKata = (stopnie) => wspolczynnikPadania(Math.cos(stopnie * RAD))

/** Współczynniki IAM dla nieba i gruntu (kąty efektywne Brandemuehla i Beckmana). */
const iamRozproszone = (nachylenie) => ({
  niebo: iamKata(59.7 - 0.1388 * nachylenie + 0.001497 * nachylenie * nachylenie),
  grunt: iamKata(90 - 0.5788 * nachylenie + 0.002693 * nachylenie * nachylenie),
})

/** Płaszczyzna z wartościami domyślnymi: nachylenie 0–180° (brak = 0), azymut 180°, 1 kWp. */
const normalizuj = (p = {}) => ({
  nachylenie: Math.min(180, Math.max(0, Number(p.nachylenie) || 0)),
  azymut: Number.isFinite(p.azymut) ? p.azymut : 180,
  mocKwp: Number.isFinite(p.mocKwp) ? p.mocKwp : 1,
})

const geometria = (plaszczyzna) => {
  const { nachylenie, azymut } = normalizuj(plaszczyzna)
  const b = nachylenie * RAD
  const g = azymut * RAD
  const iam = iamRozproszone(nachylenie)
  return {
    a1: Math.cos(b),
    a2: Math.sin(b) * Math.sin(g),
    a3: Math.sin(b) * Math.cos(g),
    niebo: ((1 + Math.cos(b)) / 2) * iam.niebo,
    grunt: ALBEDO * ((1 - Math.cos(b)) / 2) * iam.grunt,
  }
}

/** Dzień wzorcowy miesiąca co 15 min (UTC): wektory słońca i DNI przy czystym niebie. */
const probkiKalibracji = (lat, lon, m) => {
  const dzien = dzienRoku(ROK_WZORCOWY, m + 1, DZIEN_WZORCOWY)
  const polnoc = Date.UTC(ROK_WZORCOWY, m, DZIEN_WZORCOWY)
  const p = { sinH: [], cx: [], cz: [], dni: [], mianownik: 0 }
  for (let min = KROK_KALIBRACJI_MIN / 2; min < 1440; min += KROK_KALIBRACJI_MIN) {
    const s = pozycjaSlonca(polnoc + min * 60000, lat, lon)
    if (s.wysokosc > 0) {
      const { dni } = czysteNiebo(s.wysokosc, dzien, { linke: LINKE })
      const h = s.wysokosc * RAD
      const a = s.azymut * RAD
      p.sinH.push(Math.sin(h))
      p.cx.push(Math.cos(h) * Math.sin(a))
      p.cz.push(Math.cos(h) * Math.cos(a))
      p.dni.push(dni)
      p.mianownik += dni * Math.sin(h)
    }
  }
  return p
}

/** Rb płaszczyzny w dniu wzorcowym (z IAM). */
const czynnikWiazki = (p, geo) => {
  let licznik = 0
  for (let i = 0; i < p.dni.length; i += 1) {
    const c = geo.a1 * p.sinH[i] + geo.a2 * p.cx[i] + geo.a3 * p.cz[i]
    if (c > 0) licznik += p.dni[i] * c * wspolczynnikPadania(c)
  }
  return p.mianownik > 0 ? licznik / p.mianownik : 0
}

/** POA/GHI dla listy połaci o równej mocy przy udziale wiązki `fb`. */
const model = (lista, fb) => suma(lista.map((c) => fb * c.rb + (1 - fb) * c.niebo + c.grunt)) / lista.length

/* ── Kalibracja miejsca ─────────────────────────────────────────────────── */

const dopasujWiazke = (czynniki, pvgis, m) => {
  const cele = POROWNYWANE
    .filter((k) => pvgis[k][m] > 0 && pvgis.poludnie[m] > 0)
    .map((k) => ({ k, y: Math.log(pvgis[k][m] / pvgis.poludnie[m]) }))
  const blad = (fb) => {
    const ref = model(czynniki.poludnie, fb)
    return suma(cele.map(({ k, y }) => (Math.log(model(czynniki[k], fb) / ref) - y) ** 2))
  }
  let start = 0
  let min = Infinity
  for (let i = 0; i <= 20; i += 1) {
    const e = blad(i / 20)
    if (e < min) { min = e; start = i / 20 }
  }
  let a = Math.max(0, start - 0.05)
  let b = Math.min(1, start + 0.05)
  for (let i = 0; i < 30; i += 1) {
    const c1 = b - 0.618034 * (b - a)
    const c2 = a + 0.618034 * (b - a)
    if (blad(c1) < blad(c2)) b = c2; else a = c1
  }
  return (a + b) / 2
}

const pamiec = new Map()

const daneMiejsca = (lat, lon) => {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) throw new Error('uzysk: brak współrzędnych lat/lon')
  const la = Math.round(lat * 20) / 20
  const lo = Math.round(lon * 20) / 20
  const klucz = `${la}|${lo}`
  if (pamiec.has(klucz)) return pamiec.get(klucz)
  const pvgis = pvgisWPunkcie(la, lo)
  const geoUstawien = Object.fromEntries(Object.entries(USTAWIENIA_PVGIS)
    .map(([k, polacie]) => [k, polacie.map(geometria)]))
  const miesiace = Array.from({ length: 12 }, (_, m) => {
    const probki = probkiKalibracji(la, lo, m)
    const czynniki = Object.fromEntries(Object.entries(geoUstawien)
      .map(([k, lista]) => [k, lista.map((geo) => ({ ...geo, rb: czynnikWiazki(probki, geo) }))]))
    const fb = dopasujWiazke(czynniki, pvgis, m)
    return { probki, czynniki, fb, odniesienie: model(czynniki.poludnie, fb) }
  })
  const dane = { lat: la, lon: lo, pvgis, miesiace }
  if (pamiec.size >= ROZMIAR_PAMIECI) pamiec.delete(pamiec.keys().next().value)
  pamiec.set(klucz, dane)
  return dane
}

/**
 * Parametry kalibracji miejsca: tabela PVGIS w punkcie, dopasowany udział wiązki fb
 * w miesiącach i to, jak model odtwarza każde ustawienie tabeli (kWh/kWp w miesiącach).
 */
export const kalibracja = (lat, lon) => {
  const dane = daneMiejsca(lat, lon)
  const odtworzone = {}
  Object.keys(USTAWIENIA_PVGIS).forEach((k) => {
    odtworzone[k] = dane.miesiace.map((mies, m) =>
      (dane.pvgis.poludnie[m] * model(mies.czynniki[k], mies.fb)) / mies.odniesienie)
  })
  return { lat: dane.lat, lon: dane.lon, pvgis: dane.pvgis, fb: dane.miesiace.map((m) => m.fb), odtworzone }
}

/* ── Uzysk płaszczyzny ──────────────────────────────────────────────────── */

/**
 * Uzysk miesięczny (kWh z 1 kWp) płaszczyzny o nachyleniu `nachylenie` (0–90°, do 180°
 * dla strony spodniej) i azymucie prawdziwym `azymut`. `udzialWiazki` = udział wiązki
 * (z częścią okołosłoneczną) w promieniowaniu na płaszczyźnie: tyle może zabrać cień.
 */
export const uzyskMiesieczny = ({ lat, lon, nachylenie, azymut }) => {
  const dane = daneMiejsca(lat, lon)
  const geo = geometria({ nachylenie, azymut })
  const miesiace = []
  const udzialWiazki = []
  dane.miesiace.forEach((mies, m) => {
    const wiazka = mies.fb * czynnikWiazki(mies.probki, geo)
    const calosc = wiazka + (1 - mies.fb) * geo.niebo + geo.grunt
    miesiace.push((dane.pvgis.poludnie[m] * calosc) / mies.odniesienie)
    udzialWiazki.push(calosc > 0 ? wiazka / calosc : 0)
  })
  return { miesiace, rok: suma(miesiace), udzialWiazki }
}

/* ── Cień ───────────────────────────────────────────────────────────────── */

/**
 * Próbki słońca na analizę cienia: 15. dzień każdego miesiąca co `krokMin` minut czasu
 * warszawskiego, tylko słońce wyżej niż 2°. `wektor` (układ sceny, z `zbieznosc`) wywołujący
 * puszcza jako promień; `niebo` = natężenie przy czystym niebie (W/m²).
 */
export const probkiRoku = ({ lat, lon, zbieznosc = 0, krokMin = 30, rok = ROK_WZORCOWY, linke = LINKE }) => {
  const wynik = []
  for (let m = 0; m < 12; m += 1) {
    const dzien = dzienRoku(rok, m + 1, DZIEN_WZORCOWY)
    for (let min = 0; min < 1440; min += krokMin) {
      const godzina = min / 60
      const slonce = pozycjaSlonca(czasWarszawy(rok, m + 1, DZIEN_WZORCOWY, godzina), lat, lon)
      if (slonce.wysokosc > 2) {
        wynik.push({
          miesiac: m,
          godzina,
          slonce,
          wektor: wektorSlonca(slonce.azymut, slonce.wysokosc, zbieznosc),
          niebo: czysteNiebo(slonce.wysokosc, dzien, { linke }),
        })
      }
    }
  }
  return wynik
}

/**
 * Roczna strata energii z cienia (0..1) dla płaszczyzny. `cien[i]` = zasłonięta część wiązki
 * w próbce `probki[i]` (0..1). W każdym miesiącu próbki ważymy wiązką przy czystym niebie
 * na płaszczyźnie (DNI·cosθ·IAM), stratę miesiąca liczymy jako udział wiązki z kalibracji
 * razy średnie zacienienie wiązki, a miesiące ważymy uzyskiem z PVGIS.
 * Promieniowanie rozproszone (poza okołosłonecznym) uznajemy za niezacienione; miesiąc bez
 * próbek liczy się jak bez cienia.
 */
export const strataCienia = ({ lat, lon, plaszczyzna, probki = [], cien = [] }) => {
  const pl = normalizuj(plaszczyzna)
  const { miesiace, udzialWiazki } = uzyskMiesieczny({ lat, lon, ...pl })
  const wiazka = new Array(12).fill(0)
  const zacienione = new Array(12).fill(0)
  probki.forEach((p, i) => {
    const c = cosinusPadania(p.slonce, pl)
    if (c <= 0 || !(p.niebo?.dni > 0)) return
    const w = p.niebo.dni * c * wspolczynnikPadania(c)
    wiazka[p.miesiac] += w
    zacienione[p.miesiac] += w * ogranicz01(cien[i])
  })
  const energia = suma(miesiace)
  if (!(energia > 0)) return 0
  const strata = suma(miesiace.map((e, m) => (wiazka[m] > 0 ? (e * udzialWiazki[m] * zacienione[m]) / wiazka[m] : 0)))
  return strata / energia
}

/* ── Przebieg dnia ──────────────────────────────────────────────────────── */

/**
 * Typowa temperatura powietrza w godzinach dziennych (°C) w dniu roku, przybliżenie sinusoidą
 * dla Polski: ok. 0 °C w połowie stycznia, ok. 22 °C w drugiej połowie lipca (±2 °C).
 */
export const typowaTemperatura = (dzien) => 11 + 11.5 * Math.sin((2 * Math.PI * (dzien - 110)) / 365)

/**
 * Moc instalacji przy bezchmurnym niebie w ciągu doby (co `krokMin` od 0:00 do 24:00 czasu
 * warszawskiego). `plaszczyzny` = [{nachylenie, azymut, mocKwp}] (bez mocKwp: 1 kWp).
 * Promieniowanie: Ineichen i Perez + Hay i Davies, odbicia na szkle (IAM), temperatura ogniwa
 * z NOCT, potem straty systemowe `straty` (przewody, falownik, zabrudzenie). `cien(wektor, i)`
 * zwraca zasłoniętą część wiązki płaszczyzny i (0..1); zmniejsza wiązkę i część okołosłoneczną.
 * Bez `tempOtoczenia` bierzemy typową temperaturę dnia. Zwraca też moc każdej płaszczyzny.
 */
export const przebiegDnia = ({
  lat, lon, rok, miesiac, dzien, zbieznosc = 0, plaszczyzny = [], cien, krokMin = 10,
  straty = 0.14, tempOtoczenia, linke = LINKE, albedo = ALBEDO,
}) => {
  const nrDnia = dzienRoku(rok, miesiac, dzien)
  const ta = Number.isFinite(tempOtoczenia) ? tempOtoczenia : typowaTemperatura(nrDnia)
  const pl = plaszczyzny.map(normalizuj)
  const iam = pl.map((p) => iamRozproszone(p.nachylenie))
  const wynik = []
  for (let min = 0; min <= 1440; min += krokMin) {
    const godzina = min / 60
    const data = czasWarszawy(rok, miesiac, dzien, godzina)
    const slonce = pozycjaSlonca(data, lat, lon)
    let mocPlaszczyzn = pl.map(() => 0)
    if (slonce.wysokosc > 0) {
      const niebo = czysteNiebo(slonce.wysokosc, nrDnia, { linke })
      const wektor = wektorSlonca(slonce.azymut, slonce.wysokosc, zbieznosc)
      mocPlaszczyzn = pl.map((p, i) => {
        const r = naPlaszczyznie(niebo, slonce, p, albedo)
        const odslonieta = r.cosTheta > 0 && typeof cien === 'function' ? 1 - ogranicz01(cien(wektor, i)) : 1
        const iamWiazki = wspolczynnikPadania(r.cosTheta)
        const poa = (r.wiazka + r.okoloSloneczne) * odslonieta * iamWiazki
          + (r.rozproszone - r.okoloSloneczne) * iam[i].niebo + r.odbite * iam[i].grunt
        return (mocModulu({ poa, tempOtoczenia: ta, pmax: 1000 * p.mocKwp }) / 1000) * (1 - straty)
      })
    }
    wynik.push({ godzina, data, mocKw: suma(mocPlaszczyzn), slonce, mocPlaszczyzn })
  }
  return wynik
}

/** Energia z przebiegu dnia (kWh), metoda trapezów po godzinach lokalnych. */
export const energiaDnia = (przebieg) => {
  let energia = 0
  for (let i = 1; i < przebieg.length; i += 1) {
    const dt = przebieg[i].godzina - przebieg[i - 1].godzina
    if (dt > 0) energia += ((przebieg[i].mocKw + przebieg[i - 1].mocKw) / 2) * dt
  }
  return energia
}
